import { Inject } from "typescript-ioc";
import { UiComponent } from "Ui/Scripts/UiComponent";
import { UiComponentFactory } from "Ui/Scripts/UiComponentFactory";
import { EventBinder } from "Events/Scripts/EventBinder";
import { LoggerFactory } from "Logging/Scripts/LoggerFactory";
import { EndConsumerSelectorComponentView } from "./EndConsumerSelectorComponentView";
import { autobind } from "core-decorators";
import { DialogUtils } from "Shared/DialogUtils/Scripts/DialogUtils";
import { FetchService } from "Async/Scripts/FetchService";
import { Spinner } from "Spinner/Scripts/Spinner";
import { EndConsumerEditorComponentFactory } from "EndConsumerEditor/Scripts/EndConsumerEditorComponentFactory";
import * as DeliveryChannelsEventType from "ECommerce/Shopping/Delivery/Scripts/DeliveryChannelsEventType";

export class EndConsumerSelectorComponent extends UiComponent {
    public get key(): string {
        return "EndConsumerSelectorComponent";
    }

    private _view: EndConsumerSelectorComponentView;

    private _dialogUtils: DialogUtils;
    private _fetchService: FetchService;
    private readonly _endConsumerEditorComponentFactory: EndConsumerEditorComponentFactory;

    private _editorWindow: kendo.ui.Window;

    constructor(
        @Inject componentFactory: UiComponentFactory,
        @Inject binder: EventBinder,
        @Inject loggerFactory: LoggerFactory,
        @Inject dialogUtils: DialogUtils,
        @Inject fetchService: FetchService,
        @Inject endConsumerEditorComponentFactory: EndConsumerEditorComponentFactory
    ) {
        super(componentFactory, binder, loggerFactory);
        this._dialogUtils = dialogUtils;
        this._fetchService = fetchService;
        this._endConsumerEditorComponentFactory = endConsumerEditorComponentFactory;
    }

    protected init(): void {
        this._view = this.createView(EndConsumerSelectorComponentView);
        this.bindEvents();
        this.setSelectedValue();
    }

    private bindEvents(): void {
        if (this._view.createEndConsumerButton) {
            this._view.createEndConsumerButton.bindClick(this.onCreate, this.key);
        }

        for (const endConsumerRow of this._view.endConsumerRows) {
            endConsumerRow.onSelect((customerId: number, addressId: number) =>
                this.onSelect(customerId, addressId)
            );
            endConsumerRow.onEditClick((customerId: number) => this.onEdit(customerId));
            endConsumerRow.onDeleteClick((customerId: number) => this.onDelete(customerId));
        }
    }

    private setSelectedValue(): void {
        for (const endConsumerRow of this._view.endConsumerRows) {
            if (endConsumerRow.isChecked()) {
                this._view.selectedEndConsumerInput.setValue(endConsumerRow.getCustomerId());
                return;
            }
        }
        this._view.selectedEndConsumerInput.setValue("");
    }

    private getCheckedEndConsumerId(): number | undefined {
        const value: string = this._view.selectedEndConsumerInput.getValue();
        if (!value || value.length === 0) {
            return undefined;
        }
        return Number(value);
    }

    private getEndConsumerName(customerId: number): string {
        for (const endConsumerRow of this._view.endConsumerRows) {
            if (endConsumerRow.getCustomerId() === customerId) {
                return endConsumerRow.getCustomerName();
            }
        }

        return "";
    }

    @autobind
    private onSelect(customerId: number, addressId: number): void {
        this._view.selectedEndConsumerInput.setValue(customerId);
        for (const endConsumerRow of this._view.endConsumerRows) {
            endConsumerRow.change();
        }

        const data: any = {
            deliveryAddressId: addressId,
        };

        this._binder.trigger(DeliveryChannelsEventType.END_CONSUMER_ADDRESS_CHANGE, data);
    }

    @autobind
    public onCreate(): void {
        this.openEditor(
            this._view.createEndConsumerUrl +
                "?ownerCustomerId=" +
                this._view.ownerCustomerId.toString()
        );
    }

    @autobind
    private onEdit(customerId: number): void {
        this.openEditor(
            this._view.editEndConsumerUrl +
                "?ownerCustomerId=" +
                this._view.ownerCustomerId.toString() +
                "&customerId=" +
                customerId.toString()
        );
    }

    @autobind
    private onDelete(customerId: number): void {
        const messageString: string = this._view.deleteModalMessage.replace(
            "{0}",
            this.getEndConsumerName(customerId)
        );
        this._dialogUtils.confirmDeleteDialog({
            message: messageString,
            title: this._view.deleteModalTitle,

            buttonLayout: "stretched",
            cancelButtonText: this._view.deleteModalCancelText,
            okButtonText: this._view.deleteModalConfirmText,

            onOkCallback: () => {
                this.deleteCustomer(customerId);
            },
        });
    }

    @autobind
    private deleteCustomer(customerId: number): void {
        const data: FormData = new FormData();
        data.append("OwnerCustomerId", this._view.ownerCustomerId.toString());
        data.append("EndConsumerCustomerId", customerId.toString());
        Spinner.applyOverlayTo(this._context);
        this._fetchService.post(this._view.deleteEndConsumerUrl, data).then((result: any) => {
            Spinner.removeOverlayFrom(this._context);
            if (result.Success === false) {
                this._dialogUtils.errorDialog(
                    this._view.errorModalMessage,
                    this._view.errorModalTitle,
                    this._view.errorModalCloseText
                );
            }
            this._binder.trigger(DeliveryChannelsEventType.END_CONSUMER_COMPONENT_CHANGE);
        });
    }

    @autobind
    private openEditor(editorUrl: string): void {
        Spinner.applyOverlayTo(this._context);
        this._fetchService.get(editorUrl).then((result: any) => {
            this._editorWindow = kendo.createWindow(this._view.editorWindowSelector, {
                draggable: false,
                modal: true,
                open: () => {
                    Spinner.removeOverlayFrom(this._context);
                },
                resizable: false,
                title: false,
                visible: false,
            });

            this._editorWindow.content(result.toString());
            const endConsumerEditor = this._endConsumerEditorComponentFactory.create(
                this._view.editorSelector
            );
            endConsumerEditor.hideDeleteButton();
            endConsumerEditor.onChange(() => {
                this._editorWindow.close();
                this._binder.trigger(DeliveryChannelsEventType.END_CONSUMER_COMPONENT_CHANGE);
            });
            endConsumerEditor.onCancel(() => this._editorWindow.close());
            this._editorWindow.open();
            Spinner.removeOverlayFrom(this._context);
        });
    }

    @autobind
    public refresh(isDeliveryStep: boolean): void {
        Spinner.applyOverlayTo(this._context);
        const checkedEndConsumerId: number | undefined = this.getCheckedEndConsumerId();
        this._fetchService
            .get(this._view.refreshEndConsumerSelectorUrl, {
                ownerCustomerId: this._view.ownerCustomerId.toString(),
                selectedEndConsumer: checkedEndConsumerId,
                isDeliveryStep,
            })
            .then((result: any) => {
                if (this._editorWindow) {
                    this._editorWindow.destroy();
                }
                this._view.contentContainer.setHtml(result.toString());
                this.init();
                Spinner.removeOverlayFrom(this._context);
                this._binder.trigger(DeliveryChannelsEventType.END_CONSUMER_REFRESH);
            });
    }
}
